import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LockIcon from '@mui/icons-material/Lock';
import MedicationIcon from '@mui/icons-material/Medication';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { listInPhysicalCountItemsApi } from 'api/inventory';
import colors from 'common/constants/colors';
import { getLockChipTooltip } from 'common/constants/physicalCountLock';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { useInventoryAlert } from '../context/InventoryAlertContext';
import InventoryCountDialog from './InventoryCountDialog';

const InventoryCountBanner = ({ fulfillingPharmacyIds }) => {
  const reduxDispatch = useDispatch();
  const { state: alertState, dispatch: alertDispatch } = useInventoryAlert();
  const { items, loadedForKey } = alertState.physicalCount;
  const [showDialog, setShowDialog] = useState(false);

  const currentKey = fulfillingPharmacyIds.join(',');

  useEffect(() => {
    if (!fulfillingPharmacyIds?.length) return;

    alertDispatch({ type: 'PHYSICAL_COUNT_CLEAR' });
    alertDispatch({ type: 'PHYSICAL_COUNT_LOADING' });

    const key = fulfillingPharmacyIds.join(',');
    const filter =
      fulfillingPharmacyIds.length === 1
        ? { fulfillingPharmacyId: fulfillingPharmacyIds[0] }
        : undefined;

    listInPhysicalCountItemsApi({ filter, limit: 100 })
      .then((result) =>
        alertDispatch({
          type: 'PHYSICAL_COUNT_LOADED',
          items: result?.items ?? [],
          loadedForKey: key,
        })
      )
      .catch((error) => {
        reduxDispatch(handleError({ error }));
        alertDispatch({ type: 'PHYSICAL_COUNT_LOADED', items: [], loadedForKey: key });
      });
  }, [fulfillingPharmacyIds, reduxDispatch, alertDispatch, alertState.summaryRefreshKey]);

  const itemsGrouped = useMemo(() => {
    const grouped = {};
    items.forEach((item) => {
      if (!grouped[item.ndc]) {
        grouped[item.ndc] = {
          ndc: item.ndc,
          drugName: item.drugName,
          strength: item.strength,
          facilities: [],
          // POM-6112 — an NDC can appear once per facility; it's the locked NDC if any of
          // those rows is the literal lock, otherwise every row is a sibling.
          isLocked: false,
        };
      }
      grouped[item.ndc].facilities.push(item.facilityName);
      grouped[item.ndc].isLocked = grouped[item.ndc].isLocked || Boolean(item.isLocked);
    });
    return Object.values(grouped);
  }, [items]);

  const uniqueFacilityNames = useMemo(
    () => [...new Set(items.map((i) => i.facilityName))],
    [items]
  );

  const dialogFilter = useMemo(
    () =>
      fulfillingPharmacyIds.length === 1
        ? { fulfillingPharmacyId: fulfillingPharmacyIds[0] }
        : undefined,
    [fulfillingPharmacyIds]
  );

  if (loadedForKey !== currentKey || itemsGrouped.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          width: '100%',
          bgcolor: colors.inventoryLockOrangeLight,
          border: `1px solid ${colors.inventoryLockOrangeBorder}`,
          borderLeft: `4px solid ${colors.inventoryLockOrange}`,
          borderRadius: 1,
          animation: 'bannerFadeIn 0.3s ease-in',
          '@keyframes bannerFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            bgcolor: colors.inventoryLockOrange,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FactCheckIcon sx={{ fontSize: '1.4rem', color: colors.white }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.3 }}>
            Physical Count in Progress
          </Typography>
          <Typography
            sx={{
              color: colors.inventoryLockOrange,
              fontWeight: 600,
              display: 'block',
              mb: 0.75,
              fontSize: '0.75rem',
            }}
          >
            {'• '}
            {itemsGrouped.length} medication{itemsGrouped.length !== 1 ? 's' : ''}
            {' · '}
            {uniqueFacilityNames.length}{' '}
            {uniqueFacilityNames.length !== 1 ? 'facilities' : 'facility'}
            {' · active physical count'}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {itemsGrouped.slice(0, 3).map((item) => (
              <Tooltip key={item.ndc} title={getLockChipTooltip(item.isLocked)}>
                <Chip
                  icon={
                    item.isLocked ? (
                      <LockIcon sx={{ fontSize: '0.75rem !important' }} />
                    ) : (
                      <MedicationIcon sx={{ fontSize: '0.75rem !important' }} />
                    )
                  }
                  label={`${item.drugName || item.ndc}${item.strength ? ` ${item.strength}` : ''}`}
                  size='small'
                  sx={{
                    height: 22,
                    bgcolor: item.isLocked ? colors.white : 'transparent',
                    border: `1px solid ${colors.inventoryLockOrangeBorder}`,
                    borderStyle: item.isLocked ? 'solid' : 'dashed',
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 500 },
                  }}
                />
              </Tooltip>
            ))}
            {itemsGrouped.length > 3 && (
              <Chip
                label={`+${itemsGrouped.length - 3} more`}
                size='small'
                sx={{
                  height: 22,
                  bgcolor: colors.inventoryLockOrange,
                  border: `1px solid ${colors.inventoryLockOrange}`,
                  '& .MuiChip-label': {
                    px: 0.75,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: colors.white,
                  },
                }}
              />
            )}
            <Box
              sx={{
                width: '2px',
                height: 20,
                bgcolor: colors.inventoryLockOrange,
                alignSelf: 'center',
                mx: 0.5,
                flexShrink: 0,
                borderRadius: '1px',
              }}
            />
            {uniqueFacilityNames.map((name) => (
              <Chip
                key={name}
                icon={<BusinessIcon sx={{ fontSize: '0.75rem !important' }} />}
                label={name}
                size='small'
                sx={{
                  height: 22,
                  bgcolor: colors.inventoryLockOrangeBorder,
                  border: `1px solid ${colors.inventoryLockOrange}`,
                  '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 500 },
                }}
              />
            ))}
          </Box>
        </Box>

        <Button
          size='small'
          variant='outlined'
          endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
          onClick={() => setShowDialog(true)}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            px: 1.5,
            py: 0.5,
            borderColor: colors.inventoryLockOrangeBorder,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: colors.inventoryLockOrange,
              bgcolor: colors.inventoryLockOrangeLight,
            },
          }}
        >
          View Details
        </Button>
      </Box>

      <InventoryCountDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        filter={dialogFilter}
        onUnlockSuccess={() => alertDispatch({ type: 'REFRESH_SUMMARY' })}
      />
    </>
  );
};

InventoryCountBanner.propTypes = {
  fulfillingPharmacyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default InventoryCountBanner;
